import { useEffect } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { useNotificationStore } from '@/stores';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

export function NotificationDropdown() {
  const { notifications, removeNotification, clearRead, markAllAsRead } = useNotificationStore();

  // Filter to only show success notifications
  const successNotifications = notifications.filter(n => n.type === 'success');

  // Mark all success notifications as read when dropdown is opened
  useEffect(() => {
    if (successNotifications.length > 0) {
      markAllAsRead();
    }
  }, []);

  const formatTimestamp = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (successNotifications.length === 0) {
    return (
      <div className="w-96 p-6 text-center">
        <div className="text-gray-400 mb-2">
          <CheckCircle className="w-12 h-12 mx-auto" />
        </div>
        <p className="text-sm text-gray-500">No notifications</p>
      </div>
    );
  }

  return (
    <div className="w-96">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="font-semibold text-sm text-gray-900">Notifications</h3>
        {successNotifications.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearRead}
            className="text-xs h-7"
          >
            Clear Read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <ScrollArea className="max-h-[400px]">
        <div className="p-2 space-y-2">
          {successNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`flex items-start gap-2 p-3 rounded-lg border transition-opacity ${
                notification.read ? 'bg-gray-50 border-gray-200 opacity-70' : 'bg-green-50 border-green-200'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-xs">
                  {notification.title}
                </div>
                {notification.message && (
                  <div className="text-xs text-gray-700 mt-0.5">
                    {notification.message}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  {formatTimestamp(notification.timestamp)}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 flex-shrink-0"
                onClick={() => removeNotification(notification.id)}
              >
                <X className="w-3 h-3 text-gray-500" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
